import {PropsWithChildren, ReactNode} from "react";
import "./ErrorHandler.scss";
import Translate from "./shared/translation/Translate";
import portalConfiguration from "./shared/configuration/PortalConfiguration";
import {Button} from "primereact/button";
import FunctionHelper from "./shared/helper/FunctionHelper";
import AbstractSession from "./shared/session/AbstractSession";
import {mapStackTrace} from "sourcemapped-stacktrace";
import LoadingOverlay from "./shared/session/components/LoadingOverlay";
import PortalComponent from "./shared/components/PortalComponent";

export interface ErrorHandlerState {
	hasError: boolean;
	errorName: string;
	errorMessage: string;
	errorStack?: string;
	copyDone: boolean;
	copyFailed: boolean;
}

export default class ErrorHandler extends PortalComponent<PropsWithChildren<any>, ErrorHandlerState> {
	constructor(props: Readonly<PropsWithChildren<any>>) {
		super(props);

		this.state = {
			hasError: false,
			errorName: "",
			errorMessage: "",
			copyDone: false,
			copyFailed: false
		};

		this.sendSupportMessage = this.sendSupportMessage.bind(this);
		this.copyToClipboard = this.copyToClipboard.bind(this);
	}

	public static getDerivedStateFromError(error: Error) {
		// reset in ErrorHandler to use normal language detection
		portalConfiguration.fixedAdminLanguage = false;

		return {
			hasError: true
		};
	}

	/**
	 * Catches the Error thar occured in this components children and sets it as state
	 */
	public async componentDidCatch(error: Error, errorInfo: React.ErrorInfo): Promise<void> {
		let errorStack: string = await new Promise<string>((resolve) => {
			mapStackTrace(error.stack, (mappedStack: string[]) => {
				resolve(mappedStack.map(line => {
					return line.trim();
				}).join("\n"));
			}, {
				cacheGlobally: true
			});
		});

		this.setState({
			errorName: error.name,
			errorMessage: error.message,
			errorStack: errorStack
		})
	}

	/**
	 * Get the error contents as string value
	 */
	private buildErrorContent(): string {
		let content: Array<string> = [];

		if (!FunctionHelper.isEmpty(this.state.errorName)) {
			content.push(this.state.errorName);
		}

		if (!FunctionHelper.isEmpty(this.state.errorMessage)) {
			content.push(this.state.errorMessage);
		}

		if (!FunctionHelper.isEmpty(this.state.errorStack)) {
			content.push(this.state.errorStack!);
		}

		return content.join("\n\n");
	}

	/**
	 * Sends a support message with the error contents via email link
	 */
	private sendSupportMessage() {
		window.location.href = "mailto:" + portalConfiguration.supportEmail +
			"?subject=" + encodeURIComponent("Portal Exception") +
			"&body=" + encodeURIComponent(this.buildErrorContent());
	}

	/**
	 * Copies the error contents to the clipboard
	 */
	private async copyToClipboard(): Promise<void> {
		try {
			await navigator.clipboard.writeText(this.buildErrorContent());

			this.setState({
				copyDone: true,
				copyFailed: false
			});
		} catch (e) {
			// can't copy to clipboard
			this.setState({
				copyDone: false,
				copyFailed: true
			});
		}
	}

	/**
	 * render component as react node
	 */
	public render(): ReactNode {
		if (this.state.hasError) {
			let copyIcon: Array<string> = ["icon"];

			if (this.state.copyDone) {
				copyIcon.push("icon-check");
			} else if (this.state.copyFailed) {
				copyIcon.push("icon-close");
			} else {
				copyIcon.push("icon-clipboard");
			}

			// You can render any custom fallback UI
			return (
				<div className="page error-page">
					<div className="content">
						<h2>
							{Translate.value("errorscreen.title", {ns: "screen"})}
						</h2>
						{!FunctionHelper.isEmpty(this.state.errorName) &&
							<h3>{this.state.errorName}</h3>
						}
						{!FunctionHelper.isEmpty(this.state.errorMessage) &&
							<p>
								<span className="label">
									{Translate.value("errorscreen.description", {ns: "screen"})}
								</span>
								<br/>
								{this.state.errorMessage}
							</p>
						}
						{!FunctionHelper.isEmpty(this.state.errorStack) &&
							<>
								<p>
									<span className="label">
										{Translate.value("errorscreen.stack", {ns: "screen"})}
									</span>
								</p>
								<div className="stack">
									<pre>{this.state.errorStack}</pre>
								</div>
							</>
						}
						{FunctionHelper.isEmpty(this.state.errorStack) && FunctionHelper.isEmpty(this.state.errorName) &&
							<LoadingOverlay visible={true} language={"en"}/>
						}
						<div className="buttons">
							<Button className="dialog-button primary-button" icon="icon icon-mail small"
									label={Translate.value("errorscreen.buttons.support", {ns: "screen"})}
									onClick={this.sendSupportMessage}/>
							<Button className="dialog-button" icon={copyIcon.join(" ")}
									label={Translate.value("errorscreen.buttons.clipboard", {ns: "screen"})}
									onClick={this.copyToClipboard}/>
							<Button className="dialog-button" icon="icon icon-reload"
									label={Translate.value("errorscreen.buttons.reload", {ns: "screen"})}
									onClick={() => {
										window.location.reload();
									}}/>
							<Button className="dialog-button" icon="icon icon-rotate-270 small"
									label={Translate.value("errorscreen.buttons.reset", {ns: "screen"})}
									onClick={() => {
										AbstractSession.clearSessionStorage();
										window.location.reload();
									}}/>
						</div>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}